import { useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { toast } from 'react-toastify'
import { invalidateMainComponentCache } from 'src/api/main-component'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { useNavigation } from 'src/context/NavigationContext'
import { useDialogCtx, useUserCtx } from 'src/hooks/context-hooks'
import { deviationsEndpoints, invalidateDeviation, useDeleteDeviationAdmin } from '../../../api/deviation'
import { AppRoutesNavigation } from '../../../app-routes'
import UpdateDeviationAdminDialog from '../../../dialogs/deviation/UpdateDeviationAdminDialog'
import { IDeviation } from '../../../generated-types/deviation'
import { static_column } from './DeviationsTable'
import { useRowCount } from 'src/hooks/useViewportHeight'

export const AdminDeviationTable: React.FC = () => {
  const { t } = useTranslation(['deviation', 'common'])
  const { navigateTo } = useNavigation()
  const theme = useTheme()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { activePortal } = useUserCtx()
  const rowCount = useRowCount()

  const mutation = useDeleteDeviationAdmin()
  const onDelete = async (id: EntityId) => {
    showConfirmDialog(
      t('deleteDeviation'),
      t('areYouSureDeleteDeviation'),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await mutation.mutateAsync(id).then(() => {
          toast.success(t('deviationDeletedSuccessfully'))
          invalidateDeviation.getDeviationsAdmin()
          invalidateMainComponentCache.getMainComponents()
          pop()
        })
      },
    )
  }

  const updateDeviation = async (deviation: IDeviation) => {
    const updated = await showDialog(
      UpdateDeviationAdminDialog,
      {
        componentProps: {
          deviation: deviation,
        },
      },
      undefined,
      true,
    )
    if (!updated) return
  }
  const localeText = {
    toolbarQuickFilterPlaceholder: `${t('search', { ns: 'common' })}...`,
    toolbarFilters: t('filter', { ns: 'common' }),
  }
  const columns: GridColumns = useMemo(
    () => [
      ...static_column(t, theme),
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        minWidth: 60,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              key={id}
              label={t('edit', { ns: 'common' })}
              icon={<MdEdit size={24} />}
              onClick={() => updateDeviation(row)}
              color="primary"
              showInMenu
            />,
            !row.status ? (
              <GridActionsCellItem
                key={id}
                label={t('delete', { ns: 'common' })}
                icon={<MdDelete size={24} />}
                onClick={() => onDelete(id)}
                color="primary"
                showInMenu
              />
            ) : (
              <></>
            ),
          ]
        },
      },
    ],
    [],
  )
  return (
    <InfiniteScrollTable<IDeviation>
      id={`deviations-table-admin-${activePortal}`}
      columns={columns}
      defaultColumnOption={columns.map(c => ({ field: c.field, headerName: c.headerName as string }))}
      enableToolbar={true}
      queryUrl={deviationsEndpoints.getDeviationsAdmin()}
      pageSize={rowCount}
      enableFilter={true}
      onRowClick={({ id }) => navigateTo(AppRoutesNavigation.Admin.deviation(id))}
      enableQuickFilter={true}
      autoHeight={false}
      height={window.innerHeight - 210 + 'px'}
      localeText={localeText}
      getRowHeight={() => 40}
      minRowHeight="40px"
    />
  )
}
